import { HeaderSideSelector } from '@src/components/OMS/HeaderSideSelector';
import { Actions, Container, ContentRow, Section, Wrapper } from '@src/components/OMS/styles';
import { SymbolSelector } from '@src/components/SymbolSelector';
import { useCustomerMarginConfigurationsQuery } from '@src/containers/Dealer/CustomerMarginConfiguration/queries';
import { useCustomers, useCustomersByName } from '@src/hooks/useCustomer';
import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useManualTradeEntry } from '@src/hooks/useManualTradeEntry';
import { useCustomerMarketAccounts } from '@src/providers';
import { useSubAccounts } from '@src/providers/SubAccountsContext';
import {
  Alert,
  AlertVariants,
  Box,
  Button,
  ButtonVariants,
  DateTimePicker,
  Divider,
  FormControlSizes,
  FormGroup,
  HStack,
  Icon,
  IconButton,
  IconName,
  Input,
  MarketAccountStatusEnum,
  MixpanelEvent,
  NOW,
  NumberInput,
  ProductTypeEnum,
  SearchSelect,
  SideEnum,
  type SubAccount,
  Text,
  Tooltip,
  getDefaultCustomerMarketAccount,
  getDisplayNameOrNameLabel,
  isMultileg,
  isSpot,
  useAppwideDrawerContext,
  useCurrenciesContext,
  useMixpanel,
  useSecuritiesContext,
  useSecurity,
} from '@talos/kyoko';
import { identity } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v1 as uuid } from 'uuid';
import { CustomerTradeContextOverview } from './CustomerTradeContextOverview';
import {
  type CustomerTradeValidatedField,
  customerTradeFormHasErrors,
  getCustomerTradeFormFieldErrors,
} from './getCustomerTradeFormFieldErrors';
import {
  getCustomerTradeMarginRestrictionError,
  isMarketAccountMarginTradingEnabled,
} from './getCustomerTradeMarginRestrictionError';
import { getCustomerTradeQuantityCurrency } from './getCustomerTradeQuantityCurrency';
import type { BookAndModifyTradeDrawerProps } from './types';
import { useCustomerTradeDetails } from './useCustomerTradeDetails';
import { useCustomerTradeForm } from './useCustomerTradeForm';
import { useCustomerTradeSubmission } from './useCustomerTradeSubmission';
import { useManualTradeCustomerBalanceCheck } from './useManualTradeCustomerBalanceCheck';

function getSubAccountLabel(subAccount: { DisplayName: string }) {
  return subAccount.DisplayName;
}

export const BookAndModifyTradeDrawer = ({ initialForm, onClose }: BookAndModifyTradeDrawerProps) => {
  const { isOpen } = useAppwideDrawerContext();
  const { form, updateForm, resetForm } = useCustomerTradeForm(initialForm);
  const mixpanel = useMixpanel();
  const security = useSecurity(form.symbol);
  const { BaseCurrency, QuoteCurrency } = security || {};
  const { allSubAccountBooks, subAccountsByName } = useSubAccounts();
  const { currenciesList } = useCurrenciesContext();
  const {
    allowSyntheticSymbolsOnManualCustomerTrade,
    restrictCustomerTradeByMarginEnabled,
    supportNotionalMultiplierInCustomerTrade,
  } = useFeatureFlag();
  const [confirmText, setConfirmText] = useState('');
  const [touched, setTouched] = useState(false);

  const isEdit = !!form.tradeID;
  // Tracks whether the drawer has opened.
  const wasOpenRef = useRef(false);
  // Prevents successful submissions from being tracked as cancellations.
  const skipCancelTrackingRef = useRef(false);
  const { isRequestingTradeDetails } = useCustomerTradeDetails({
    isOpen,
    tradeID: form.tradeID,
    updateForm,
  });

  const resetOnClose = useCallback(() => {
    skipCancelTrackingRef.current = false;
    wasOpenRef.current = false;
    resetForm();
    setConfirmText('');
    setTouched(false);
  }, [resetForm]);

  // The drawer remains mounted when closed, so this effect handles cleanup and cancellation tracking.
  useEffect(() => {
    if (isOpen) {
      wasOpenRef.current = true;
      return;
    }

    // Skip the initial closed render; there is nothing to reset yet.
    if (!wasOpenRef.current) {
      return;
    }

    // Track cancellation only when a new trade closes without submitting.
    if (!skipCancelTrackingRef.current && !isEdit) {
      mixpanel.track(MixpanelEvent.CancelNewCustomerTrade);
    }
    resetOnClose();
  }, [isEdit, isOpen, mixpanel, resetOnClose]);

  const {
    data: customerMarginConfigurations,
    isError: isCustomerMarginConfigurationsError,
    isFetching: isFetchingCustomerMarginConfigurations,
  } = useCustomerMarginConfigurationsQuery({
    enabled: restrictCustomerTradeByMarginEnabled && isOpen,
  });

  const isMarginTradingEnabled = useMemo(
    () => isMarketAccountMarginTradingEnabled(customerMarginConfigurations, form.marketAccount),
    [customerMarginConfigurations, form.marketAccount]
  );

  const fieldErrors = useMemo(() => {
    const errors = getCustomerTradeFormFieldErrors(form, isEdit);
    if (
      !restrictCustomerTradeByMarginEnabled ||
      isCustomerMarginConfigurationsError ||
      isFetchingCustomerMarginConfigurations ||
      !customerMarginConfigurations ||
      !form.marketAccount ||
      !form.symbol ||
      isMarginTradingEnabled == null
    ) {
      return errors;
    }

    const marginRestrictionError = getCustomerTradeMarginRestrictionError({
      isSpotSecurity: security ? isSpot(security) : undefined,
      isMarginTradingEnabled,
    });
    return marginRestrictionError ? { ...errors, symbol: marginRestrictionError } : errors;
  }, [
    customerMarginConfigurations,
    form,
    isCustomerMarginConfigurationsError,
    isEdit,
    isFetchingCustomerMarginConfigurations,
    isMarginTradingEnabled,
    restrictCustomerTradeByMarginEnabled,
    security,
  ]);
  const hasFieldErrors = customerTradeFormHasErrors(fieldErrors);
  const isMarginRestrictionUnavailable =
    restrictCustomerTradeByMarginEnabled &&
    isOpen &&
    (isFetchingCustomerMarginConfigurations ||
      isCustomerMarginConfigurationsError ||
      customerMarginConfigurations == null);

  const showFieldError = useCallback(
    (field: CustomerTradeValidatedField) => (touched ? fieldErrors[field] : undefined),
    [fieldErrors, touched]
  );

  const { displayCurrency, requestCurrency } = getCustomerTradeQuantityCurrency(
    security,
    supportNotionalMultiplierInCustomerTrade
  );
  const quantityCurrency = displayCurrency ?? BaseCurrency;

  const { onRecompute, amountWarningText, feeWarningText } = useManualTradeEntry({
    quantity: form.quantity,
    amount: form.amount,
    price: form.price,
    symbol: form.symbol,
    fee: form.fee,
    feeBPS: form.feeBPS,
    feeCurrency: form.feeCurrency,
    quantityCurrency,
    updateForm,
  });

  const handleClickRecompute = useCallback(
    (field?: 'quantity' | 'amount' | 'price' | 'fee' | 'feeBPS') => onRecompute(field),
    [onRecompute]
  );

  const handleOnGenerateID = useCallback(() => {
    updateForm({ clTradeID: uuid() });
  }, [updateForm]);

  const customersByName = useCustomersByName();
  const getCustomerLabel = useCallback(
    (customerName: string) => customersByName?.get(customerName)?.DisplayName || customerName,
    [customersByName]
  );
  const customers = useCustomers();
  const customerOptions = useMemo(() => (customers ? customers.map(c => c.Name) : []), [customers]);

  const { searchableSecurities } = useSecuritiesContext();
  const availableSecurities = useMemo(
    () =>
      searchableSecurities.filter(
        searchableSecurity =>
          !isMultileg(searchableSecurity) &&
          (allowSyntheticSymbolsOnManualCustomerTrade || searchableSecurity.ProductType !== ProductTypeEnum.Synthetic)
      ),
    [allowSyntheticSymbolsOnManualCustomerTrade, searchableSecurities]
  );

  const handleSymbolChanged = useCallback((symbol?: string) => updateForm({ symbol: symbol ?? '' }), [updateForm]);

  const { customerMarketAccountsByName, getCustomerMarketAccounts } = useCustomerMarketAccounts();
  const customerMarketAccounts = useMemo(() => {
    if (!form.counterparty) {
      return [];
    }

    return (
      getCustomerMarketAccounts(form.counterparty)
        // DEAL-3733: Only show active accounts
        ?.filter(ma => ma.Status === MarketAccountStatusEnum.Active)
        .map(ma => ma.Name) || []
    );
  }, [form.counterparty, getCustomerMarketAccounts]);
  const customerAccountLabel =
    getDisplayNameOrNameLabel(customerMarketAccountsByName.get(form.marketAccount ?? '')) || form.marketAccount || '';

  const feeCurrencyOptions = useMemo(() => currenciesList.map(c => c.Symbol), [currenciesList]);

  // Default to QuoteCurrency if Symbol is set.
  useEffect(() => {
    if (form.symbol && QuoteCurrency && !form.feeCurrency) {
      updateForm({ feeCurrency: QuoteCurrency });
    }
  }, [QuoteCurrency, form.feeCurrency, form.symbol, updateForm]);

  const handleCounterpartyUpdate = useCallback(
    (newCounterparty?: string) => {
      if (!newCounterparty) {
        updateForm({ marketAccount: undefined, counterparty: undefined });
        return;
      }

      // Select the active default account for the selected customer.
      const newMktAcc = getDefaultCustomerMarketAccount(getCustomerMarketAccounts(newCounterparty))?.Name;
      updateForm({ marketAccount: newMktAcc, counterparty: newCounterparty });
    },
    [getCustomerMarketAccounts, updateForm]
  );

  const handleSubAccountOnChange = useCallback(
    (selection?: SubAccount) => updateForm({ riskSubAccount: selection?.Name }),
    [updateForm]
  );

  const canComputeFeeFromBPS = [quantityCurrency, QuoteCurrency].filter(Boolean).includes(form.feeCurrency);

  const handleFeeCurrencyChanged = useCallback(
    (feeCurrency?: string) => {
      const supportsFeeBPS = [quantityCurrency, QuoteCurrency].filter(Boolean).includes(feeCurrency);
      updateForm(supportsFeeBPS ? { feeCurrency } : { feeCurrency, feeBPS: '' });
    },
    [quantityCurrency, QuoteCurrency, updateForm]
  );

  // Trigger FeeBps to compute on modification with fees.
  useEffect(() => {
    if (form.fee && canComputeFeeFromBPS && !form.feeBPS && isEdit) {
      onRecompute();
    }
  }, [canComputeFeeFromBPS, form.fee, form.feeBPS, isEdit, onRecompute]);

  const { node, invalidField } = useManualTradeCustomerBalanceCheck({ form, security });

  const handleClear = useCallback(() => {
    resetForm();
    setConfirmText('');
    setTouched(false);
  }, [resetForm]);

  const isSubmissionBlocked =
    hasFieldErrors ||
    !BaseCurrency ||
    !QuoteCurrency ||
    isMarginRestrictionUnavailable ||
    isRequestingTradeDetails ||
    (!!invalidField && confirmText.toLowerCase() !== 'confirm');

  const handleSubmitSuccess = useCallback(() => {
    skipCancelTrackingRef.current = true;
    onClose();
  }, [onClose]);

  const { submit, isSubmitting } = useCustomerTradeSubmission({
    form,
    isEdit,
    isBlocked: isSubmissionBlocked,
    isOpen,
    quoteCurrency: QuoteCurrency,
    requestCurrency,
    onSuccess: handleSubmitSuccess,
  });

  const handleSubmit = useCallback(() => {
    setTouched(true);
    void submit();
  }, [submit]);

  const handleCancel = useCallback(() => onClose(), [onClose]);

  const feeBpsSuffix = (
    <HStack gap="spacing4" mr="spacing2">
      BPS
      {feeWarningText != null && (
        <Tooltip tooltip="Re-compute Fee BPS">
          <IconButton
            size={FormControlSizes.Small}
            variant={ButtonVariants.Default}
            onClick={() => handleClickRecompute('feeBPS')}
            icon={IconName.Refresh}
            data-testid="fee-bps-refresh"
          />
        </Tooltip>
      )}
    </HStack>
  );

  return (
    <Wrapper
      data-testid="customer-trade-drawer"
      onSubmit={event => {
        event.preventDefault();
        void handleSubmit();
      }}
    >
      <Container minHeight={0} data-testid="customer-trade-drawer-container">
        <Section data-testid="customer-trade-drawer-sections" textAlign="left" minWidth={0} gap="spacing16">
          {isCustomerMarginConfigurationsError && (
            <Alert
              children="Unable to load customer margin configurations. Close and reopen the drawer to retry."
              dismissable={false}
              variant={AlertVariants.Negative}
              data-testid="margin-configuration-error"
              mb="spacing12"
            />
          )}

          {isEdit ? (
            <CustomerTradeContextOverview
              customer={getCustomerLabel(form.counterparty)}
              customerAccount={customerAccountLabel}
              security={security}
              symbol={form.symbol}
              tradeID={form.tradeID ?? ''}
            />
          ) : (
            <ContentRow data-testid="customer-trade-drawer-customer-context">
              <FormGroup label="Customer" required error={showFieldError('counterparty')}>
                <SearchSelect
                  selection={form.counterparty}
                  options={customerOptions}
                  getLabel={getCustomerLabel}
                  onChange={handleCounterpartyUpdate}
                  placeholder="Customer"
                  invalid={!!showFieldError('counterparty')}
                  data-testid="customer"
                />
              </FormGroup>
              <FormGroup label="Customer Account" required error={showFieldError('marketAccount')}>
                <SearchSelect
                  selection={form.marketAccount}
                  options={customerMarketAccounts}
                  onChange={marketAccount => updateForm({ marketAccount })}
                  getLabel={identity}
                  placeholder="Customer Account"
                  disabled={customerMarketAccounts.length === 1 && form.marketAccount != null}
                  invalid={!!showFieldError('marketAccount')}
                  data-testid="customer-account"
                />
              </FormGroup>
              <FormGroup label="Symbol" required error={showFieldError('symbol')}>
                <SymbolSelector
                  symbol={form.symbol || undefined}
                  availableSecurities={availableSecurities}
                  onSymbolChanged={handleSymbolChanged}
                  disabled={isSubmitting}
                  invalid={!!showFieldError('symbol')}
                  touched={touched}
                  portalize
                  showFavorites
                  showGroups
                  showClear
                  showAddMultiLegButton={false}
                  closeOnInputBlur={false}
                  startOpen={false}
                  maxHeight={480}
                  centerBasicSelect
                  data-testid="symbol"
                />
              </FormGroup>
            </ContentRow>
          )}

          <ContentRow data-testid="customer-trade-drawer-side">
            <HeaderSideSelector
              showTwoWay={false}
              side={form.side}
              onChange={side => updateForm({ side })}
              disabled={isEdit}
            />
          </ContentRow>

          <ContentRow data-testid="customer-trade-drawer-terms">
            <FormGroup label="Quantity" required error={showFieldError('quantity')}>
              <NumberInput
                min="0"
                value={form.quantity}
                placeholder="Quantity"
                invalid={invalidField === 'quantity' || !!showFieldError('quantity')}
                onChange={quantity => updateForm({ quantity })}
                disabled={!form.symbol || isSubmitting}
                onBlur={() => onRecompute()}
                suffix={
                  <HStack gap="spacing4" mr="spacing2">
                    {displayCurrency}
                    {amountWarningText != null && (
                      <Tooltip tooltip="Re-compute Quantity">
                        <IconButton
                          size={FormControlSizes.Small}
                          variant={ButtonVariants.Default}
                          onClick={() => handleClickRecompute('quantity')}
                          icon={IconName.Refresh}
                        />
                      </Tooltip>
                    )}
                  </HStack>
                }
                data-testid="quantity"
              />
            </FormGroup>
            <FormGroup label="Price" required error={showFieldError('price')}>
              <NumberInput
                min="0"
                value={form.price}
                placeholder="Price"
                invalid={!!showFieldError('price')}
                onChange={price => updateForm({ price })}
                disabled={!form.symbol || isSubmitting}
                onBlur={() => onRecompute()}
                suffix={
                  <HStack gap="spacing4" mr="spacing2">
                    {QuoteCurrency}
                    {amountWarningText != null && (
                      <Tooltip tooltip="Re-compute Price">
                        <IconButton
                          size={FormControlSizes.Small}
                          variant={ButtonVariants.Default}
                          onClick={() => handleClickRecompute('price')}
                          icon={IconName.Refresh}
                        />
                      </Tooltip>
                    )}
                  </HStack>
                }
                data-testid="price"
              />
            </FormGroup>
            <FormGroup label="Amount" required error={showFieldError('amount')}>
              <NumberInput
                min="0"
                invalid={invalidField === 'amount' || !!showFieldError('amount')}
                value={form.amount}
                placeholder="Amount"
                onChange={amount => updateForm({ amount })}
                disabled={!form.symbol || isSubmitting}
                onBlur={() => onRecompute()}
                suffix={
                  <HStack gap="spacing4" mr="spacing2">
                    {QuoteCurrency}
                    {amountWarningText != null && (
                      <Tooltip tooltip="Re-compute Amount">
                        <IconButton
                          size={FormControlSizes.Small}
                          variant={ButtonVariants.Default}
                          onClick={() => handleClickRecompute('amount')}
                          icon={IconName.Refresh}
                        />
                      </Tooltip>
                    )}
                  </HStack>
                }
                data-testid="amount"
              />
              {amountWarningText && (
                <Alert
                  children={amountWarningText}
                  dismissable={false}
                  variant={AlertVariants.Warning}
                  data-testid="amount-warning"
                  mt="spacing16"
                />
              )}
            </FormGroup>
          </ContentRow>

          <Divider mx="-spacing16" />

          <ContentRow data-testid="customer-trade-drawer-fees">
            <FormGroup label="Commission" error={showFieldError('salesCommission')}>
              <NumberInput
                value={form.salesCommission ?? ''}
                placeholder="Commission"
                invalid={!!showFieldError('salesCommission')}
                onChange={salesCommission => updateForm({ salesCommission })}
                disabled={!form.symbol || isRequestingTradeDetails || isSubmitting}
                suffix="BPS"
                data-testid="commission"
              />
            </FormGroup>
            <FormGroup label="Fee" error={showFieldError('fee')}>
              <NumberInput
                value={form.fee ?? ''}
                placeholder="Fee"
                invalid={!!showFieldError('fee')}
                onBlur={() => onRecompute()}
                onChange={fee => updateForm({ fee })}
                disabled={!form.symbol || isSubmitting}
                data-testid="fee"
                suffix={
                  <HStack gap="spacing4" mr="spacing2">
                    <SearchSelect
                      selection={form.feeCurrency}
                      options={feeCurrencyOptions}
                      onChange={handleFeeCurrencyChanged}
                      getLabel={identity}
                      placeholder="Currency"
                      size={FormControlSizes.Small}
                      dropdownPlacement="bottom-start"
                      dropdownWidth="150px"
                      data-testid="fee-currency"
                    />
                    {feeWarningText != null && (
                      <Tooltip tooltip="Re-compute Fee">
                        <IconButton
                          size={FormControlSizes.Small}
                          variant={ButtonVariants.Default}
                          onClick={() => handleClickRecompute('fee')}
                          icon={IconName.Refresh}
                          data-testid="fee-refresh"
                        />
                      </Tooltip>
                    )}
                  </HStack>
                }
              />
              {feeWarningText && (
                <Alert
                  children={feeWarningText}
                  dismissable={false}
                  variant={AlertVariants.Warning}
                  data-testid="fee-warning"
                  mt="spacing16"
                />
              )}
            </FormGroup>
            <FormGroup label="Fee BPS" error={showFieldError('feeBPS')}>
              {canComputeFeeFromBPS ? (
                <NumberInput
                  value={form.feeBPS ?? ''}
                  placeholder="Fee"
                  invalid={!!showFieldError('feeBPS')}
                  onBlur={() => onRecompute()}
                  onChange={feeBPS => updateForm({ feeBPS })}
                  disabled={isSubmitting}
                  data-testid="fee-bps"
                  suffix={feeBpsSuffix}
                />
              ) : (
                <Input
                  inputType="text"
                  type="number"
                  value="N/A"
                  disabled
                  data-testid="fee-bps"
                  suffix={feeBpsSuffix}
                />
              )}
            </FormGroup>
          </ContentRow>

          <Divider mx="-spacing16" />

          <ContentRow data-testid="customer-trade-drawer-details">
            <FormGroup label="External ID" required={!isEdit} error={showFieldError('clTradeID')}>
              <Input
                inputType="text"
                value={form.clTradeID}
                placeholder="External ID"
                onChange={e => updateForm({ clTradeID: e.target.value })}
                disabled={isEdit || isSubmitting}
                invalid={!!showFieldError('clTradeID')}
                suffix={
                  <Button
                    size={FormControlSizes.Small}
                    variant={ButtonVariants.Default}
                    onClick={handleOnGenerateID}
                    disabled={isEdit || isSubmitting}
                    ghost
                  >
                    Generate
                  </Button>
                }
                data-testid="external-id"
              />
            </FormGroup>
            <FormGroup label="Trade Time">
              <DateTimePicker
                shortcuts={{ [NOW]: 'Now' }}
                onChange={date => updateForm({ transactTime: date || undefined })}
                value={form.transactTime || null}
                portalize={false}
                data-testid="trade-time"
                placeholder="Optional"
              />
            </FormGroup>
            <FormGroup label="Comments">
              <Input
                value={form.comments}
                placeholder="Optional"
                onChange={e => updateForm({ comments: e.target.value })}
                disabled={isSubmitting}
                data-testid="comments"
              />
            </FormGroup>

            {(allSubAccountBooks?.length ?? 0) > 0 && (
              <FormGroup
                label={
                  <HStack gap="spacing4">
                    <Text>Risk SubAccount</Text>
                    <Tooltip
                      tooltip="(Optional) This is the subaccount for managing the risk associated with this trade"
                      usePortal={false}
                    >
                      <Icon icon={IconName.QuestionMarkCircle} />
                    </Tooltip>
                  </HStack>
                }
              >
                <SearchSelect
                  disabled={isRequestingTradeDetails || isSubmitting}
                  id="manual-trade-subaccount"
                  getLabel={getSubAccountLabel}
                  options={allSubAccountBooks!}
                  selection={form.riskSubAccount ? subAccountsByName?.get(form.riskSubAccount) : undefined}
                  onChange={handleSubAccountOnChange}
                  data-testid="risk-sub-account"
                />
              </FormGroup>
            )}
          </ContentRow>
        </Section>
      </Container>

      {!!node && (
        <Box flex="0 0 auto" p="spacing16" borderTop="1px solid" borderColor="colors.gray.000">
          <ContentRow data-testid="customer-trade-drawer-trade-confirmation-footer">
            <FormGroup>
              <Alert
                children={node}
                variant={AlertVariants.Negative}
                dismissable={false}
                data-testid="available-balance-warning"
              />
            </FormGroup>
            <FormGroup label='To create the trade, type "confirm" below'>
              <Input
                value={confirmText}
                autoComplete="off"
                onChange={e => setConfirmText(e.target.value)}
                disabled={isSubmitting}
                data-testid="trade-confirmation-input"
              />
            </FormGroup>
          </ContentRow>
        </Box>
      )}

      <Actions
        data-testid="customer-trade-drawer-actions"
        display="grid"
        gridTemplateColumns="minmax(min-content, 116px) 1fr"
      >
        <Button
          disabled={isSubmitting}
          onClick={isEdit ? handleCancel : handleClear}
          data-testid="customer-trade-drawer-clear"
        >
          {isEdit ? 'Cancel' : 'Clear'}
        </Button>
        <span onMouseEnter={() => setTouched(true)}>
          <Button
            disabled={
              hasFieldErrors ||
              isMarginRestrictionUnavailable ||
              isRequestingTradeDetails ||
              isSubmitting ||
              (!!invalidField && confirmText.toLowerCase() !== 'confirm')
            }
            onClick={handleSubmit}
            data-testid="customer-trade-drawer-submit"
            variant={form.side === SideEnum.Buy ? ButtonVariants.Positive : ButtonVariants.Negative}
          >
            {isSubmitting ? 'Sending trade...' : isEdit ? 'Update Trade' : 'Book Trade'}
          </Button>
        </span>
      </Actions>
    </Wrapper>
  );
};
